import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { api } from '@/App';
import { 
  Users,
  Search,
  Loader2,
  Mail,
  Phone,
  Calendar,
  BookOpen,
  User,
  CheckCircle,
  XCircle,
  Eye,
  RefreshCw
} from 'lucide-react';

const PortalUsers = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userBookings, setUserBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  useEffect(() => {
    fetchPortalUsers();
  }, []);

  const fetchPortalUsers = async () => {
    try {
      const response = await api.get('/portal/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch portal users:', error);
      toast.error('Failed to load portal users');
    } finally {
      setLoading(false);
    }
  };

  const viewUserDetails = async (user) => {
    setSelectedUser(user);
    setDetailsDialogOpen(true);
    setLoadingBookings(true);
    
    try {
      const response = await api.get(`/portal/admin/users/${user.id}/bookings`);
      setUserBookings(response.data);
    } catch (error) {
      console.error('Failed to fetch user bookings:', error);
      setUserBookings([]);
    } finally {
      setLoadingBookings(false);
    }
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.phone?.includes(searchQuery)
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6 text-orange-500" />
            Portal Users
          </h1>
          <p className="text-gray-500 mt-1">Users who signed up on the public booking portal</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-base px-3 py-1">
            {users.length} {users.length === 1 ? 'User' : 'Users'}
          </Badge>
          <Button variant="outline" onClick={fetchPortalUsers}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, email or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-portal-users"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">User</TableHead>
                <TableHead className="font-semibold">Phone</TableHead>
                <TableHead className="font-semibold">Email</TableHead>
                <TableHead className="font-semibold text-center">Verified</TableHead>
                <TableHead className="font-semibold text-center">Bookings</TableHead>
                <TableHead className="font-semibold">Signed Up</TableHead>
                <TableHead className="font-semibold text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <Users className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">No portal users found</p>
                    <p className="text-sm text-gray-400 mt-1">Users will appear here once they sign up on the portal</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.id} className="hover:bg-gray-50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                          <User className="h-5 w-5 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{user.name || 'Guest'}</p>
                          <p className="text-xs text-gray-500">ID: {user.id?.substring(0, 8)}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-gray-600">
                        <Phone className="h-3.5 w-3.5" />
                        <span className="text-sm">{user.phone || 'N/A'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-gray-600">
                        <Mail className="h-3.5 w-3.5" />
                        <span className="text-sm">{user.email || 'N/A'}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {user.is_verified ? (
                        <Badge className="bg-green-100 text-green-700 border border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Verified
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                          <XCircle className="h-3 w-3 mr-1" />
                          Pending
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        <BookOpen className="h-3 w-3 mr-1" />
                        {user.booking_count || 0}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-gray-600">
                        <Calendar className="h-3.5 w-3.5" />
                        <span className="text-sm">{formatDate(user.created_at)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => viewUserDetails(user)}
                        data-testid={`view-user-${user.id}`}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* User Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-orange-500" />
              User Details
            </DialogTitle>
            <DialogDescription>
              Portal user information and booking history
            </DialogDescription>
          </DialogHeader>

          {selectedUser && (
            <div className="space-y-6 py-4">
              {/* User Info */}
              <Card>
                <CardContent className="py-4">
                  <div className="flex items-center gap-4">
                    <div className="h-16 w-16 rounded-full bg-orange-100 flex items-center justify-center">
                      <User className="h-8 w-8 text-orange-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold">{selectedUser.name || 'Guest'}</h3>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Phone className="h-4 w-4" />
                          {selectedUser.phone}
                        </div>
                        <div className="flex items-center gap-1">
                          <Mail className="h-4 w-4" />
                          {selectedUser.email || 'No email'}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        {selectedUser.is_verified ? (
                          <Badge className="bg-green-100 text-green-700">Verified</Badge>
                        ) : (
                          <Badge variant="outline" className="bg-yellow-50 text-yellow-700">Pending Verification</Badge>
                        )}
                        <span className="text-xs text-gray-500">
                          Signed up: {formatDateTime(selectedUser.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Bookings */}
              <div>
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  Booking History ({userBookings.length})
                </h3>
                
                {loadingBookings ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
                  </div>
                ) : userBookings.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center">
                      <BookOpen className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                      <p className="text-gray-500">No bookings yet</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {userBookings.map((booking) => (
                      <Card key={booking.id}>
                        <CardContent className="py-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{booking.property_name || 'Property'}</p>
                              <p className="text-sm text-gray-500">
                                {formatDate(booking.check_in)} - {formatDate(booking.check_out)}
                              </p>
                            </div>
                            <div className="text-right">
                              <Badge className={
                                booking.status === 'checked_out' ? 'bg-gray-100 text-gray-700' :
                                booking.status === 'checked_in' ? 'bg-green-100 text-green-700' :
                                booking.status === 'reserved' ? 'bg-blue-100 text-blue-700' :
                                booking.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-700'
                              }>
                                {booking.status?.replace('_', ' ')}
                              </Badge>
                              <p className="text-sm font-medium mt-1">₹{booking.total_amount?.toLocaleString()}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortalUsers;
